import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { TextAlign } from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import './BriefEditor.css';

const BriefEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [bedrijf, setBedrijf] = useState(null);
  const [titel, setTitel] = useState('');
  const [onderwerp, setOnderwerp] = useState('');
  const [klant, setKlant] = useState(null);
  const [klanten, setKlanten] = useState([]);
  const [showKlantPicker, setShowKlantPicker] = useState(false);
  const [showVerstuurModal, setShowVerstuurModal] = useState(false);
  const [verstuurOntvangers, setVerstuurOntvangers] = useState([{ naam: '', email: '', telefoon: '' }]);
  const [verstuurStatus, setVerstuurStatus] = useState('idle');
  const [showToFormsModal, setShowToFormsModal] = useState(false);
  const [toFormsStrength, setToFormsStrength] = useState('aes');
  const [toFormsLoading, setToFormsLoading] = useState(false);
  const [toFormsError, setToFormsError] = useState(null);
  const [verstuurMethode, setVerstuurMethode] = useState('aes');
  const [verstuurMessage, setVerstuurMessage] = useState('');
  const [zoekKlant, setZoekKlant] = useState('');
  const [geadresseerdeType, setGeadresseerdeType] = useState('klant');
  const [monteur, setMonteur] = useState(null);
  const [monteurs, setMonteurs] = useState([]);
  const [showMonteurPicker, setShowMonteurPicker] = useState(false);
  const [zoekMonteur, setZoekMonteur] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle');
  const saveTimer = useRef(null);
  const token = localStorage.getItem('token');

  const editor = useEditor({
    extensions: [
      StarterKit, Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle, Color,
      Table.configure({ resizable: true }), TableRow, TableCell, TableHeader,
      Placeholder.configure({ placeholder: 'Begin hier met de inhoud van je brief…' })
    ],
    content: ''
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/brieven/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (cancelled) return;
        if (data.success) {
          if (data.brief) {
            setBrief(data.brief);
            setTitel(data.brief.titel || '');
            setOnderwerp(data.brief.onderwerp || '');
            setKlant(data.brief.klant);
            if (data.brief.voor_gebruiker_id) {
              setGeadresseerdeType('monteur');
              try {
                const gr = await fetch(`/api/gebruikers/${data.brief.voor_gebruiker_id}`, { headers: { Authorization: `Bearer ${token}` } });
                const gd = await gr.json();
                const g = gd.gebruiker || gd;
                if (g && g.id) setMonteur(g);
              } catch (e) {}
            }
          }
          if (data.bedrijf) setBedrijf(data.bedrijf);
        }
      } catch (err) { console.error('Kon brief niet laden:', err); }
    })();
    return () => { cancelled = true; if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [id, token]);

  useEffect(() => {
    if (editor && brief) editor.commands.setContent(brief.html_content || '');
  }, [editor, brief]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/klanten', { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (cancelled) return;
        if (data.success) setKlanten(data.klanten || data.data || []);
        else if (Array.isArray(data)) setKlanten(data);
      } catch (err) { console.error('Kon klanten niet laden:', err); }

      try {
        const res = await fetch('/api/gebruikers', { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.success) setMonteurs(data.gebruikers || data.data || []);
        else if (Array.isArray(data)) setMonteurs(data);
      } catch (err) { console.error('Kon personeel niet laden:', err); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const saveNow = useCallback(async (changes) => {
    setSaveStatus('saving');
    try {
      const res = await fetch(`/api/brieven/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(changes)
      });
      const data = await res.json();
      setSaveStatus(data.success ? 'saved' : 'error');
      if (data.success) setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) { console.error('Save failed:', err); setSaveStatus('error'); }
  }, [id, token]);

  const scheduleSave = useCallback((changes) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveNow(changes), 1500);
  }, [saveNow]);

  useEffect(() => {
    if (!editor) return;
    const handler = () => scheduleSave({ html_content: editor.getHTML() });
    editor.on('update', handler);
    return () => { editor.off('update', handler); };
  }, [editor, scheduleSave]);

  const onTitelChange = (e) => { setTitel(e.target.value); scheduleSave({ titel: e.target.value }); };
  const onOnderwerpChange = (e) => { setOnderwerp(e.target.value); scheduleSave({ onderwerp: e.target.value }); };

  const selectKlant = (k) => {
    setKlant({
      id: k.id, naam: k.naam, email: k.email, telefoon: k.telefoon,
      adres: k.adres, postcode: k.postcode, plaats: k.plaats,
      partner_naam: k.partner_naam, bedrijf_naam: k.bedrijf_naam
    });
    setShowKlantPicker(false);
    setZoekKlant('');
    scheduleSave({ klant_id: k.id });

    // Auto-vul de aanhef met de klant-naam (alleen als 'Beste ...,' nog in body staat)
    if (editor) {
      const html = editor.getHTML();
      const safeNaam = (k.naam || '').replace(/[<>&"]/g, '');
      const newHtml = html.replace(/Beste\s+[^,<]+,/, 'Beste ' + safeNaam + ',');
      if (newHtml !== html) {
        editor.commands.setContent(newHtml);
      }
    }
  };

  const removeKlant = () => { setKlant(null); scheduleSave({ klant_id: null }); };

  const selectMonteur = (m) => {
    setMonteur({
      id: m.id,
      voornaam: m.voornaam,
      achternaam: m.achternaam,
      email: m.email,
      telefoon: m.telefoon
    });
    setShowMonteurPicker(false);
    setZoekMonteur('');
    scheduleSave({ voor_gebruiker_id: m.id, klant_id: null });
  };

  const removeMonteur = () => { setMonteur(null); scheduleSave({ voor_gebruiker_id: null }); };

  const filteredMonteurs = monteurs.filter(m => {
    if (!zoekMonteur) return true;
    const q = zoekMonteur.toLowerCase();
    return (m.voornaam || '').toLowerCase().includes(q) || (m.achternaam || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q);
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL invoeren:', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const insertTable = () => editor && editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  const setColor = (e) => editor && editor.chain().focus().setColor(e.target.value).run();
  const insertHR = () => editor && editor.chain().focus().setHorizontalRule().run();

  const downloadPdf = () => window.print();

  const openVerstuurModal = () => {
    if (geadresseerdeType === 'klant' && klant) {
      setVerstuurOntvangers([{ naam: klant.naam || '', email: klant.email || '', telefoon: klant.telefoon || '' }]);
    } else if (geadresseerdeType === 'monteur' && monteur) {
      const naam = ((monteur.voornaam || '') + ' ' + (monteur.achternaam || '')).trim();
      setVerstuurOntvangers([{ naam: naam, email: monteur.email || '', telefoon: monteur.telefoon || '' }]);
    }
    setVerstuurStatus('idle');
    setVerstuurMessage('');
    setShowVerstuurModal(true);
  };

  const submitToForms = async () => {
    setToFormsLoading(true);
    setToFormsError(null);
    try {
      const res = await fetch(`/api/brieven/${id}/to-forms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ signing_strength: toFormsStrength })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Naar Forms mislukt');
      window.location.href = '/forms';
    } catch (e) {
      setToFormsError(e.message);
      setToFormsLoading(false);
    }
  };
  
  const submitVerstuur = async () => {
    for (const o of verstuurOntvangers) {
      if (!o.naam || !o.email) {
        setVerstuurMessage('Vul naam en email in voor elke ontvanger');
        setVerstuurStatus('error');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(o.email)) {
        setVerstuurMessage('Ongeldig email-adres: ' + o.email);
        setVerstuurStatus('error');
        return;
      }
    }
    setVerstuurStatus('sending');
    setVerstuurMessage('Bezig met versturen...');
    try {
      const res = await fetch('/api/brieven/' + id + '/verstuur', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ ontvangers: verstuurOntvangers, mode: verstuurMethode })
      });
      const data = await res.json();
      if (data.success) {
        setVerstuurStatus('success');
        setVerstuurMessage('Brief verstuurd! Klant krijgt magic-link in mail. 📧');
        setTimeout(() => { setShowVerstuurModal(false); navigate('/brieven'); }, 2500);
      } else {
        setVerstuurStatus('error');
        setVerstuurMessage(data.message || 'Onbekende fout');
      }
    } catch (err) {
      setVerstuurStatus('error');
      setVerstuurMessage('Netwerkfout: ' + err.message);
    }
  };

  if (!editor) return <div className="brief-editor-loading">Editor laden…</div>;

  const StatusBadge = () => {
    if (saveStatus === 'saving') return <span className="brief-status saving">💾 Opslaan…</span>;
    if (saveStatus === 'saved') return <span className="brief-status saved">✓ Opgeslagen</span>;
    if (saveStatus === 'error') return <span className="brief-status error">⚠ Fout</span>;
    return null;
  };

  const TbBtn = ({ active, disabled, onClick, title, children }) => (
    <button type="button" className={active ? 'active' : ''} disabled={disabled} onClick={onClick} title={title}>{children}</button>
  );

  const filteredKlanten = klanten.filter(k => !zoekKlant || (k.naam && k.naam.toLowerCase().includes(zoekKlant.toLowerCase())));
  const today = new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="brief-editor-page">
      <div className="brief-editor-topbar">
        <button className="brief-btn-back" onClick={() => navigate('/brieven')}>← Terug</button>
        <input className="brief-titel-input" value={titel} onChange={onTitelChange} placeholder="Naam van de brief (intern)" />
        <StatusBadge />
        <button className="brief-btn-pdf" onClick={downloadPdf} title="Download als PDF">📄 PDF</button>
        <button className="brief-btn-verstuur" onClick={() => setShowToFormsModal(true)} title="Plaats in Forms">📋 Naar Forms</button>
      </div>

      <div className="brief-meta-row">
        <div className="brief-meta-block">
          <label className="brief-meta-label">Geadresseerde</label>
          <div style={{display:'flex',gap:6,marginBottom:8}}>
            <button onClick={() => setGeadresseerdeType('klant')} style={{flex:1,padding:'6px 10px',fontSize:12,border:'1px solid #cbd5e1',borderRadius:4,background:geadresseerdeType==='klant'?'#3b82f6':'white',color:geadresseerdeType==='klant'?'white':'#1e293b',cursor:'pointer'}}>📋 Klant</button>
            <button onClick={() => setGeadresseerdeType('monteur')} style={{flex:1,padding:'6px 10px',fontSize:12,border:'1px solid #cbd5e1',borderRadius:4,background:geadresseerdeType==='monteur'?'#3b82f6':'white',color:geadresseerdeType==='monteur'?'white':'#1e293b',cursor:'pointer'}}>👤 Personeel</button>
          </div>
          {geadresseerdeType === 'klant' ? (
            klant ? (
              <div className="brief-klant-selected">
                <div className="brief-klant-naam">{klant.naam}</div>
                <button className="brief-btn-link" onClick={removeKlant}>verwijder</button>
              </div>
            ) : (
              <button className="brief-btn-pick" onClick={() => setShowKlantPicker(true)}>+ Klant kiezen</button>
            )
          ) : (
            monteur ? (
              <div className="brief-klant-selected">
                <div className="brief-klant-naam">{((monteur.voornaam || '') + ' ' + (monteur.achternaam || '')).trim()}</div>
                <button className="brief-btn-link" onClick={removeMonteur}>verwijder</button>
              </div>
            ) : (
              <button className="brief-btn-pick" onClick={() => setShowMonteurPicker(true)}>+ Personeel kiezen</button>
            )
          )}
        </div>
        <div className="brief-meta-block grow">
          <label className="brief-meta-label">Onderwerp</label>
          <input className="brief-onderwerp-input" value={onderwerp} onChange={onOnderwerpChange} placeholder="Bijv. Offerte voor opdracht XYZ" />
        </div>
      </div>

      {showKlantPicker && (
        <div className="brief-klant-modal" onClick={() => setShowKlantPicker(false)}>
          <div className="brief-klant-modal-inner" onClick={(e) => e.stopPropagation()}>
            <div className="brief-klant-modal-head">
              <h3>Kies klant</h3>
              <button className="brief-btn-link" onClick={() => setShowKlantPicker(false)}>✕</button>
            </div>
            <input className="brief-zoek-input" autoFocus value={zoekKlant} onChange={(e) => setZoekKlant(e.target.value)} placeholder="Zoek op naam…" />
            <div className="brief-klant-list">
              {filteredKlanten.length === 0 ? (
                <p className="brief-klant-empty">Geen klanten gevonden.</p>
              ) : filteredKlanten.map(k => (
                <div key={k.id} className="brief-klant-row" onClick={() => selectKlant(k)}>
                  <div className="brief-klant-row-naam">{k.naam}</div>
                  <div className="brief-klant-row-meta">{k.plaats || k.email || ''}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      
            {showMonteurPicker && (
        <div className="brief-klant-modal" onClick={() => setShowMonteurPicker(false)}>
          <div className="brief-klant-modal-inner" onClick={(e) => e.stopPropagation()}>
            <div className="brief-klant-modal-head">
              <h3>Kies personeel</h3>
              <button className="brief-btn-link" onClick={() => setShowMonteurPicker(false)}>✕</button>
            </div>
            <input className="brief-zoek-input" autoFocus value={zoekMonteur} onChange={(e) => setZoekMonteur(e.target.value)} placeholder="Zoek op naam of email…" />
            <div className="brief-klant-list">
              {filteredMonteurs.length === 0 ? (
                <p className="brief-klant-empty">Geen personeel gevonden.</p>
              ) : filteredMonteurs.map(m => (
                <div key={m.id} className="brief-klant-row" onClick={() => selectMonteur(m)}>
                  <div className="brief-klant-row-naam">{((m.voornaam || '') + ' ' + (m.achternaam || '')).trim()}</div>
                  <div className="brief-klant-row-meta">{m.email || ''}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

{showToFormsModal && (
        <div className="brief-verstuur-modal" onClick={() => !toFormsLoading && setShowToFormsModal(false)}>
          <div className="brief-verstuur-modal-content" onClick={e => e.stopPropagation()}>
            <h3>📋 Brief plaatsen in Forms</h3>
            <p>Deze brief wordt een Forms-template. Daarna versturen via standaard Forms-flow.</p>
            <div style={{margin:'16px 0', padding:12, background:'#f7f9fc', borderRadius:6, border:'1px solid #e3e8ef'}}>
              <strong style={{display:'block', marginBottom:10}}>Hoe wil je dat de klant tekent?</strong>
              <label style={{display:'block', marginBottom:8, cursor:'pointer'}}>
                <input type="radio" name="tfs" value="aes" checked={toFormsStrength === 'aes'} onChange={e => setToFormsStrength(e.target.value)} style={{marginRight:8}} />
                <strong>🔐 Met AES (SMS-OTP)</strong> — formeel, geverifieerd
              </label>
              <label style={{display:'block', cursor:'pointer'}}>
                <input type="radio" name="tfs" value="basic" checked={toFormsStrength === 'basic'} onChange={e => setToFormsStrength(e.target.value)} style={{marginRight:8}} />
                <strong>✍️ Gewone handtekening</strong> — klant tekent zonder OTP
              </label>
            </div>
            {toFormsError && <div style={{color:'#c0392b', marginBottom:12, padding:8, background:'#fdf1f0', borderRadius:4}}>⚠️ {toFormsError}</div>}
            <div className="brief-verstuur-actions">
              <button onClick={() => setShowToFormsModal(false)} disabled={toFormsLoading}>Annuleren</button>
              <button onClick={submitToForms} disabled={toFormsLoading} className="brief-btn-verstuur">
                {toFormsLoading ? 'Bezig...' : '📋 Plaats in Forms'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVerstuurModal && (
        <div className="brief-verstuur-modal" onClick={() => verstuurStatus !== 'sending' && setShowVerstuurModal(false)}>
          <div className="brief-verstuur-modal-inner" onClick={(e) => e.stopPropagation()}>
            <div className="brief-verstuur-modal-head">
              <h3>📧 Verstuur ter ondertekening</h3>
              <button className="brief-btn-link" onClick={() => setShowVerstuurModal(false)} disabled={verstuurStatus === 'sending'}>✕</button>
            </div>
            <p className="brief-verstuur-uitleg">
              De ontvanger krijgt een mail met magic-link → tekent via AES SMS-OTP.
              De getekende brief verschijnt automatisch in <strong>Forms Inbox</strong>.
            </p>
            {verstuurOntvangers.map((o, i) => (
              <div key={i} className="brief-verstuur-rij">
                <div>
                  <label>Naam</label>
                  <input value={o.naam} onChange={(e) => { const u = [...verstuurOntvangers]; u[i] = { ...u[i], naam: e.target.value }; setVerstuurOntvangers(u); }} placeholder="Jan Test" />
                </div>
                <div>
                  <label>Email</label>
                  <input type="email" value={o.email} onChange={(e) => { const u = [...verstuurOntvangers]; u[i] = { ...u[i], email: e.target.value }; setVerstuurOntvangers(u); }} placeholder="jan@test.nl" />
                </div>
                <div>
                  <label>Telefoon (SMS-OTP)</label>
                  <input type="tel" value={o.telefoon} onChange={(e) => { const u = [...verstuurOntvangers]; u[i] = { ...u[i], telefoon: e.target.value }; setVerstuurOntvangers(u); }} placeholder="+31612345678" />
                </div>
              </div>
            ))}
            {verstuurMessage && (<div className={'brief-verstuur-message ' + verstuurStatus}>{verstuurMessage}</div>)}
            <div className="brief-verstuur-actions">
              <button onClick={() => setShowVerstuurModal(false)} disabled={verstuurStatus === 'sending'}>Annuleren</button>
              <button className="brief-btn-primary" onClick={submitVerstuur} disabled={verstuurStatus === 'sending' || verstuurStatus === 'success'}>
                {verstuurStatus === 'sending' ? 'Versturen...' : '📧 Verstuur'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="brief-toolbar">
        <TbBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Vet"><strong>B</strong></TbBtn>
        <TbBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Cursief"><em>I</em></TbBtn>
        <TbBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Onderstreept"><u>U</u></TbBtn>
        <TbBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Doorhalen"><s>S</s></TbBtn>
        <TbBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline code"><code>&lt;/&gt;</code></TbBtn>
        <span className="sep">|</span>
        <TbBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Kop 1">H1</TbBtn>
        <TbBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Kop 2">H2</TbBtn>
        <TbBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Kop 3">H3</TbBtn>
        <span className="sep">|</span>
        <TbBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Opsomming">•</TbBtn>
        <TbBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Genummerd">1.</TbBtn>
        <TbBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote">❝</TbBtn>
        <span className="sep">|</span>
        <TbBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Links uitlijnen">⬅</TbBtn>
        <TbBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Centreren">≡</TbBtn>
        <TbBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Rechts uitlijnen">➡</TbBtn>
        <TbBtn onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Uitvullen">☰</TbBtn>
        <span className="sep">|</span>
        <TbBtn onClick={setLink} active={editor.isActive('link')} title="Link toevoegen">🔗</TbBtn>
        <label className="brief-color-picker" title="Tekstkleur">🎨<input type="color" onChange={setColor} defaultValue="#000000" /></label>
        <TbBtn onClick={insertTable} title="Tabel invoegen (3×3)">📊</TbBtn>
        <TbBtn onClick={insertHR} title="Horizontale lijn">➖</TbBtn>
        <span className="sep">|</span>
        
        
        <span className="sep">|</span>
        <TbBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Ongedaan">↶</TbBtn>
        <TbBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Opnieuw">↷</TbBtn>
      </div>

      <div className="brief-paper">
        <div className="brief-paper-header">
          {bedrijf && bedrijf.logo_url ? (
            <img src={bedrijf.logo_url} alt={bedrijf.naam} className="brief-paper-logo" />
          ) : (
            <div className="brief-paper-logo-placeholder">[ Logo niet ingesteld ]</div>
          )}
          {klant && (
            <div className="brief-paper-klant">
              <div className="brief-paper-klant-naam">{klant.naam}</div>
              {klant.bedrijf_naam && <div>{klant.bedrijf_naam}</div>}
              {klant.adres && <div>{klant.adres}</div>}
              {(klant.postcode || klant.plaats) && (<div>{klant.postcode} {klant.plaats}</div>)}
            </div>
          )}
        </div>

        <div className="brief-paper-datum">
          {bedrijf && bedrijf.plaats ? `${bedrijf.plaats}, ` : ''}{today}
        </div>

        {onderwerp && (
          <div className="brief-paper-onderwerp"><strong>Onderwerp:</strong> {onderwerp}</div>
        )}

        <div className="brief-editor-content">
          <EditorContent editor={editor} />
        </div>

        {bedrijf && (
          <div className="brief-paper-footer">
            <div className="brief-footer-line">
              <strong>{bedrijf.naam}</strong>
              {bedrijf.kvk_nummer && <span> · KvK {bedrijf.kvk_nummer}</span>}
              {bedrijf.website && <span> · {bedrijf.website}</span>}
            </div>
            <div className="brief-footer-line">
              {bedrijf.adres && <span>{bedrijf.adres}</span>}
              {(bedrijf.postcode || bedrijf.plaats) && <span>, {bedrijf.postcode} {bedrijf.plaats}</span>}
            </div>
            <div className="brief-footer-line">
              {bedrijf.email && <span>{bedrijf.email}</span>}
              {bedrijf.telefoonnummer && <span> · {bedrijf.telefoonnummer}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BriefEditor;
